import { TransformSource } from './command-manager';
import type { IOTTransform } from '@tencent/slide-sdk-ot-transform';
import type { ITransformCommand } from '@tencent/slide-sdk-pb-model';
import type { ILogger } from './logger.interface';

const MODULE_NAME = '[SDK][CM][TTM]';

export enum TaskType {
    Local = 'local',
    Collaboration = 'collaboration',
}

export class TransformTaskManager {
    private transformPromise: Promise<{
        followCollaborationCommands: ITransformCommand[];
        followLocalCommands: ITransformCommand[];
    }> | null = null;
    private taskQueue: Array<{ commands: ITransformCommand[]; taskType: TaskType }> = [];

    public constructor(private transformManager: IOTTransform, private logger: ILogger) {}

    public get isTransforming() {
        return this.transformPromise !== null;
    }

    public addTask(commands: ITransformCommand[], taskType: TaskType) {
        this.logger.info('[SDK][Task]', 'add Task:', commands, 'type:', taskType);
        this.taskQueue.push({ commands, taskType });
    }

    public startTransform() {
        if (!this.transformPromise) {
            this.transformPromise = this.transform();
        }
        return this.transformPromise;
    }

    private async transform() {
        const transformRes: {
            followCollaborationCommands: ITransformCommand[];
            followLocalCommands: ITransformCommand[];
        } = {
            followCollaborationCommands: [],
            followLocalCommands: [],
        };
        while (this.taskQueue.length) {
            let followCollaborationCommands: ITransformCommand[] = [];
            let followLocalCommands: ITransformCommand[] = [];
            const { commands, taskType } = this.taskQueue.shift()!;
            const collaborationCommands: ITransformCommand[] =
                taskType === TaskType.Collaboration ? commands : transformRes.followCollaborationCommands;
            const localCommands: ITransformCommand[] =
                taskType === TaskType.Local ? commands : transformRes.followLocalCommands;
            try {
                // 如果转换有一边完全是空的，就没必要进转换
                const shouldIgnore =
                    !collaborationCommands.filter((command) => !!command.mutations?.length).length ||
                    !localCommands.filter((command) => !!command.mutations?.length).length;
                if (shouldIgnore) {
                    [followCollaborationCommands, followLocalCommands] = await [collaborationCommands, localCommands];
                } else {
                    this.logger.info(MODULE_NAME, 'continue transform:', collaborationCommands, localCommands);
                    const res = await this.transformManager.transform(
                        collaborationCommands,
                        localCommands,
                        false,
                        TransformSource.Offline,
                    );
                    followCollaborationCommands = res.transformedA;
                    followLocalCommands = res.transformedB;
                    this.logger.info(
                        MODULE_NAME,
                        'transform success:',
                        followCollaborationCommands,
                        followLocalCommands,
                    );
                }
                if (taskType === TaskType.Local) {
                    transformRes.followCollaborationCommands = followCollaborationCommands;
                    transformRes.followLocalCommands.push(...followLocalCommands);
                } else {
                    transformRes.followCollaborationCommands.push(...followCollaborationCommands);
                    transformRes.followLocalCommands = followLocalCommands;
                }
            } catch (e: any) {
                this.logger.error(MODULE_NAME, 'transform error:', e.toString());
                this.taskQueue.splice(0, this.taskQueue.length);
                throw e;
            }
        }
        this.transformPromise = await null;
        return transformRes;
    }
}
